import { useDispatch } from "react-redux";
import "./DCFormFillEditor.css";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../ds2/components";
import { setLastViewedMessageId } from "../../../redux/features/appData";
import { AgentResponseMessage } from "winslow-core";
import { FileText, File, Edit3, Download, Pin, PinOff, Eye, X } from "lucide-react";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/features/appData";
import { apiToggleMessagePin } from "../../../services/AIService";
import { updateSpecificMessage } from "../../../redux/features/agent/agentSlice";
import toast from "react-hot-toast";
import { useState } from "react";
import FileViewer from "react-file-viewer";

type DCFormFillEditorProps = {
  message: AgentResponseMessage;
  isOutdated?: boolean;
  title: string;
  cloudURL: string;
  formFields: string[];
};

export const DCFormFillEditor = ({
  message,
  isOutdated = false,
  title,
  cloudURL,
  formFields,
}: DCFormFillEditorProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storeUser = useSelector(selectUser);
  const [showPreview, setShowPreview] = useState(false);

  // Get file extension and determine file type
  const fileExtension = cloudURL.split(".").pop()?.toLowerCase();
  const isWordDocument = fileExtension === "doc" || fileExtension === "docx";
  const isPdf = fileExtension === "pdf";

  // Get file type icon
  const getFileTypeIcon = () => {
    if (isWordDocument) {
      return <FileText className="file-type-icon word" size={24} />;
    } else if (isPdf) {
      return <File className="file-type-icon pdf" size={24} />;
    }
    return <FileText className="file-type-icon generic" size={24} />;
  };

  // Get file type label
  const getFileTypeLabel = () => {
    if (isWordDocument) {
      return fileExtension?.toUpperCase();
    } else if (isPdf) {
      return "PDF";
    }
    return fileExtension?.toUpperCase() || "FILE";
  };

  const handleEditForm = () => {
    // Create search parameters
    const params = new URLSearchParams({
      title: title,
      cloudURL: cloudURL,
      formFields: JSON.stringify(formFields),
    });

    dispatch(setLastViewedMessageId(message.id));

    // Navigate to form fill editor with parameters using React Router
    navigate(`/waz/form-fill-editor?${params.toString()}`);
  };

  const handleDownload = () => {
    // Open the cloudURL in a new tab for download
    window.open(cloudURL, "_blank");
  };

  const handleTogglePin = async () => {
    if (!storeUser?.id) return;

    try {
      const updatedMessage = await apiToggleMessagePin(storeUser.id, message.id);
      if (updatedMessage) {
        dispatch(updateSpecificMessage(updatedMessage));
        toast.success(message.pinned ? "Message unpinned" : "Message pinned");
      }
    } catch (error) {
      console.error("Error toggling pin:", error);
      toast.error("Failed to toggle pin");
    }
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
  };

  return (
    <>
      <div className="dc-form-fill-editor-widget">
        {/* Pin Icon */}
        <Button
          variant="tertiary"
          size="sm"
          onClick={handleTogglePin}
          title={message.pinned ? "Unpin message" : "Pin message"}
        >
          {message.pinned ? <Pin size={12} /> : <PinOff size={12} />}
        </Button>
        
        {/* <button
          className={`pin-button ${message.pinned ? 'pinned' : ''}`}
          onClick={handleTogglePin}
          title={message.pinned ? "Unpin message" : "Pin message"}
        >
          {message.pinned ? <Pin size={12} /> : <PinOff size={12} />}
        </button> */}

        <div className="widget-header">
          <div className="file-info">
            {getFileTypeIcon()}
            <span className="file-type-label">{getFileTypeLabel()}</span>
            <div className="file-details">
              <h3 className="document-title">{title}</h3>
            </div>
          </div>
          <div className="header-actions">
            <div className="field-count-badge">
              {formFields.length} field{formFields.length !== 1 ? "s" : ""}
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleEditForm}
              title="Edit form fields"
            >
              <Edit3 size={18} />
              <span>Edit Form</span>
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handlePreview}
              title="Preview document">
              <Eye size={18} />
              <span>Preview</span>
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleDownload}
              title="Download document">
              <Download size={18} />
              <span>Download</span>
            </Button>
            {/* <button
              className="action-button edit-button"
              onClick={handleEditForm}
              title="Edit form fields">
              <Edit3 size={18} />
              <span>Edit Form</span>
            </button> */}
            {/* <button
              className="action-button preview-button"
              onClick={handlePreview}
              title="Preview document">
              <Eye size={18} />
              <span>Preview</span>
            </button> */}
            {/* <button
              className="action-button download-button"
              onClick={handleDownload}
              title="Download document">
              <Download size={18} />
              <span>Download</span>
            </button> */}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="preview-modal-overlay" onClick={handleClosePreview}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-modal-header">
              <h3 className="preview-modal-title">{title}</h3>
              <button
                className="preview-modal-close"
                onClick={handleClosePreview}
                title="Close preview">
                <X size={20} />
              </button>
            </div>
            <div className="preview-modal-content">
              <FileViewer
                fileType={fileExtension || "pdf"}
                filePath={cloudURL}
                errorComponent={(error: any) => (
                  <div className="preview-error">
                    <p>Error loading document: {error}</p>
                    <a
                      href={cloudURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="preview-link">
                      Open document in new tab
                    </a>
                  </div>
                )}
                onError={(error: any) =>
                  console.error("[DCFormFillEditor] FileViewer error:", error)
                }
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
